import type { RefObject } from 'react';
import { forwardRef, memo, useCallback, useEffect } from 'react';

import { useRefIntercept } from '../../hooks/useRefIntercept';
import { isCtrlOrMetaPressed } from '../../utils/web';
import type { SearchInputFieldProps } from '../Form/SearchInput';
import { SearchInputField } from '../Form/SearchInput';
import styles from './quickSearch.module.scss';

export interface SearchInputRefProps extends SearchInputFieldProps {}

const excludedSelector = '.codex-editor';

// Optimization: Utility function to check if the target is within a selector
const targetIsInSelector = (event: KeyboardEvent, selector: string): boolean => {
  return !!(
    event.target &&
    Array.from(document.querySelectorAll<HTMLElement>(selector)).some(elt =>
      elt.contains(event.target as Node),
    )
  );
};

export const SearchInput = memo(
  forwardRef<HTMLInputElement, SearchInputRefProps>(({ children, value, ...inputProps }, refFn) => {
    const { showBackButton, onClickBack } = inputProps;
    const [refToForward, ref] = useRefIntercept(refFn);

    const focusInput = useCallback(
      (event: KeyboardEvent) => {
        if (
          isCtrlOrMetaPressed(event) &&
          event.code === 'KeyK' &&
          !targetIsInSelector(event, excludedSelector)
        ) {
          event.preventDefault();
          const elt = (ref as RefObject<HTMLInputElement>).current;

          // Triggers the onFocus event, which is expected to show the search details.
          elt?.focus();
        }
      },
      [ref],
    );

    const blurInput = useCallback(
      (event: KeyboardEvent) => {
        // Catch Escape: trigger the "back" handler to request hiding the search details.
        if (event.key === 'Escape') {
          const elt = (ref as RefObject<HTMLInputElement>).current;
          if (showBackButton) {
            elt?.blur();
            onClickBack?.();
          }
        }
      },
      [onClickBack, ref, showBackButton],
    );

    useEffect(() => {
      document.addEventListener('keydown', focusInput);
      document.addEventListener('keydown', blurInput);
      return () => {
        document.removeEventListener('keydown', focusInput);
        document.removeEventListener('keydown', blurInput);
      };
    }, [focusInput, blurInput]);

    return (
      <SearchInputField
        ref={refToForward}
        className={styles.searchContainer}
        value={value as string}
        {...inputProps}
      />
    );
  }),
);
